/**
 * User geolocation helpers.
 *
 * requestUserLocation()  – asks the browser for permission, caches {lat,lng}
 *                          in sessionStorage, returns a Promise<{lat,lng}>.
 * getStoredLocation()    – returns the cached {lat,lng} or null.
 * getStoredLocationId()  – returns the cached nearest restaurant ID or null.
 * setStoredLocationId()  – persists the nearest restaurant ID to sessionStorage.
 * haversineDistance()    – great-circle distance in miles.
 * getNearestRestaurant() – returns the closest restaurant from an array.
 */

const SESSION_KEY = 'userLatLng';
const SESSION_LOCATION_ID_KEY = 'mowgli_location_id';

/**
 * Request the user's location from the browser.
 * Stores the result in sessionStorage for the lifetime of the tab.
 *
 * @returns {Promise<{lat: number, lng: number}>}
 */
export function requestUserLocation() {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			reject(new Error('Geolocation is not supported by this browser.'));
			return;
		}

		// Return cached value immediately if already granted this session
		const cached = getStoredLocation();
		if (cached) {
			resolve(cached);
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const coords = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				};
				sessionStorage.setItem(SESSION_KEY, JSON.stringify(coords));
				resolve(coords);
			},
			(error) => {
				reject(error);
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
		);
	});
}

/**
 * Read the stored location from sessionStorage.
 *
 * @returns {{lat: number, lng: number}|null}
 */
export function getStoredLocation() {
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

/**
 * Read the stored nearest restaurant ID from sessionStorage.
 *
 * @returns {number|null}
 */
export function getStoredLocationId() {
	try {
		const raw = sessionStorage.getItem(SESSION_LOCATION_ID_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

/**
 * Persist the nearest restaurant ID to sessionStorage.
 *
 * @param {number} id
 */
export function setStoredLocationId(id) {
	sessionStorage.setItem(SESSION_LOCATION_ID_KEY, JSON.stringify(id));
}

/**
 * Haversine formula – returns the great-circle distance in miles
 * between two lat/lng points.
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} distance in miles
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
	const R = 3958.8; // Earth's radius in miles
	const toRad = (deg) => (deg * Math.PI) / 180;

	const dLat = toRad(lat2 - lat1);
	const dLng = toRad(lng2 - lng1);

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) *
			Math.cos(toRad(lat2)) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

/**
 * Find the nearest restaurant to the user's stored location.
 *
 * Each restaurant in the array must have `lat` and `lng` properties.
 * Any additional properties (name, slug, etc.) are passed through.
 *
 * @param {Array<{lat: number, lng: number, [key: string]: any}>} restaurants
 * @returns {{restaurant: object, distanceKm: number}|null}
 */
export function getNearestRestaurant(restaurants) {
	const userCoords = getStoredLocation();
	if (!userCoords || !Array.isArray(restaurants) || restaurants.length === 0) {
		return null;
	}

	let nearest = null;
	let minDistance = Infinity;

	for (const restaurant of restaurants) {
		const distMiles = haversineDistance(
			userCoords.lat,
			userCoords.lng,
			restaurant.lat,
			restaurant.lng
		);
		if (distMiles < minDistance) {
			minDistance = distMiles;
			nearest = restaurant;
		}
	}

	return nearest ? { restaurant: nearest, distanceMiles: minDistance } : null;
}
